import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { LocalStorageService } from 'ngx-webstorage';
import { User } from '@clearroadlab/cam';

import { environment } from '../../environments/environment';
import { filterParams, handleAPIError, ISearchParams } from '../utils';

const path = `${environment.camApi.baseUrl}/users`;
const authPathSmartCar = `${environment.authApi.baseUrl}/accounts/smartcar`;

const profileStorageKey = 'up';
const tokenStorageKey = 'ut';

export class UserSearchResult {
  count = 0;
  results: User[] = [];
}

interface IUserSearchParams extends ISearchParams {
  search?: string;
  searchIn?: string[];
  programKey?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private _user = new ReplaySubject<User>(1);
  private _token = '';

  /**
   * Redirect after login.
   */
  public redirectUrl: string;
  public user: User;

  constructor(
    private http: HttpClient,
    private router: Router,
    private localStorage: LocalStorageService
  ) {
    this.getCurrentUser();
    // Subscribe to the _user subject to keep this.user updated
    this._user.subscribe(user => this.user = user);
  }

  isSuperAdmin(): boolean {
    return this.user && this.user.invitationCode === environment.superAdmin.key;
  }

  private async getCurrentUser() {
    try {
      this._token = this.localStorage.retrieve(tokenStorageKey);
      this._user.next(this.localStorage.retrieve(profileStorageKey));
      // fetch user to make sure data is up to date
      await this.reloadUser();
    } catch (_err) {
      // if we are using a wrong token
      if (this._token) {
        await this.signOut();
      }
    }
  }

  private async reloadUser() {
    const user = await this.get();
    if (user) {
      await this.updateCurrentUser(user);
    }
  }

  public updateCurrentUser(user: User) {
    const { token, ...profile } = user;
    this._user.next(profile);
    this.localStorage.store(profileStorageKey, profile);
    this._token = token;
    this.localStorage.store(tokenStorageKey, token);
  }

  public get user$() {
    return this._user.asObservable();
  }

  public get loggedInUser() {
    return this._user.pipe(take(1)).toPromise();
  }

  public get headers() {
    return {
      'X-ACCESS-TOKEN': this._token
    };
  }

  public get authHeaders(){
    return new HttpHeaders({
      'x-access-token': environment.authApi.accessToken
    });
  }

  public async signIn(user: Partial<User>) {
    const res = await this.http.post<User>(`${path}/signin`, user).toPromise().catch(handleAPIError);
    if (!res.admin) {
      throw new Error('not-found');
    }
    try {
      this.updateCurrentUser(res);
    } catch (err) {
      console.error(err);
    }
  }

  public signOut() {
    this._user.next(null);
    this.localStorage.clear(profileStorageKey);
    this.localStorage.clear(tokenStorageKey);
    return this.router.navigate(['/users', 'signin']);
  }

  public async get() {
    try {
      return await this.http.get<User>(`${path}/me`, {
        headers: this.headers
      }).toPromise().catch(handleAPIError);
    } catch (err) {
      if (err.message === 'Unauthorized') {
        await this.signOut();
      } else {
        throw err;
      }
    }
  }

  public getUser<T extends User>(email: string) {
    return this.http.get<T>(`${path}/${email}`, {
      headers: this.headers
    }).toPromise().catch(handleAPIError);
  }

  public list(params?: IUserSearchParams) {
    return this.http.get<UserSearchResult>(path, {
      headers: this.headers,
      params: filterParams({
        admin: true,
        ...params
      })
    }).toPromise().catch(handleAPIError);
  }

  public update(user: Partial<User>) {
    return this.http.put<User>(`${path}/${user.email}`, user, {
      headers: this.headers
    }).toPromise().catch(handleAPIError);
  }

  public delete({ email }: Partial<User>) {
    return this.http.delete(`${path}/${email}`, {
      headers: this.headers
    }).toPromise().catch(handleAPIError);
  }

  public anonymize(user: Partial<User>) {
    if (!user) {
      throw new Error('User cannot be null or undefined');
    }

    return this.http.post<User>(`${path}/anonymize`,user, {
      headers: this.headers
    })
      .toPromise()
      .catch((err) => {
        if (err.status === 404) {
          throw new Error('User not found');
        } else {
          throw handleAPIError(err);
        }
      });
  }

  async resetPassword(user: Partial<User>) {
    return this.http.post<User>(`${path}/password/reset`, user).toPromise().catch(handleAPIError);
  }

  public getUsersWithToken() {
    return this.http.get<User[]>(path + '/list-with-token', {
      headers: this.headers
    }).toPromise().catch(handleAPIError);
  }

  async notificationAll(title, text) {
    return this.http.post(`${path}/`, { title, text }, {
      headers: this.headers
    }).toPromise().catch(handleAPIError);
  }

  async findSmartCar(email: string) {
    return this.http.get(authPathSmartCar, {
      headers:this.authHeaders,
      params: { email }
    }).toPromise().catch(handleAPIError);
  }

  public async adjustment(data: any[]): Promise<any> {
    const url = `${path}/adjustments`;
    try {
      const response = await this.http.post(url, data, {
        headers: this.headers
      }).toPromise();
      return response;
    } catch (error) {
      handleAPIError(error);
      throw error;  // Re-throwing the error allows the caller to handle it as well
    }
  }
}
